/**
 * Advisory corroboration: does a LinkedIn page name plausibly belong to the
 * entity we're about to tag?
 *
 * The manual URN-confirm path (review queue + Entities → Manage) otherwise
 * trusts the operator entirely — the reverse-check only proves the URN and the
 * page are internally consistent, NOT that the page is the right *company* for
 * this entity. This catches the "confirmed a plausible-but-wrong page" mistake
 * (same/similar name, a personal profile, a different firm sharing a word).
 *
 * This is a deliberately-approximate, SOFT signal — it drives an amber warning,
 * never a hard block, because legitimate brand names diverge from the recorded
 * name (e.g. "Memphasys Reproductive Biotechnology" vs "Memphasys Limited").
 * It mirrors the core rules of the backend `entity_name_corroborates`
 * (jit_entity_refresh.py); exact parity is not required for an advisory hint.
 * Fail-open: when we can't tell, we do NOT warn.
 */

const GENERIC_TOKENS = new Set([
  'limited', 'ltd', 'ltd.', 'inc', 'inc.', 'corp', 'corporation', 'plc', 'nl',
  'pty', 'group', 'holdings', 'company', 'co', 'the', 'and', '&',
  'resources', 'mining', 'minerals', 'metals', 'energy', 'gold', 'limited.',
]);

function norm(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function distinctiveTokens(name: string): string[] {
  return (name || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t && t.length >= 2 && !GENERIC_TOKENS.has(t));
}

/**
 * True when `pageName` plausibly corroborates the entity (its recorded name
 * and/or ticker). Returns true (fail-open) when inputs are too sparse to judge.
 */
export function pageNameCorroboratesEntity(
  pageName: string | null | undefined,
  entityName: string | null | undefined,
  ticker: string | null | undefined,
): boolean {
  const page = (pageName || '').trim();
  const entity = (entityName || '').trim();
  if (!page || !entity) return true; // nothing to judge → don't warn

  const pageNorm = norm(page);
  const entityNorm = norm(entity);
  if (!pageNorm || !entityNorm) return true;

  // Normalised equality — "GBM Resources Ltd" ~ "GBM Resources".
  if (pageNorm === entityNorm) return true;

  // Ticker appears as a word in the page name — "…(ASX: AQN)".
  const code = (ticker || '').trim();
  if (code.length >= 2) {
    const re = new RegExp(`\\b${code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(page)) return true;
  }

  const entTokens = distinctiveTokens(entity);
  if (entTokens.length === 0) return true; // suffix filter ate the whole name → don't warn
  const pageTokens = distinctiveTokens(page);
  const pageJoined = pageNorm;

  for (const et of entTokens) {
    for (const pt of pageTokens) {
      // token equality, prefix fusion ("ionicre" ⊃ "ionic"), shared 4-char stem
      if (
        et === pt ||
        pt.startsWith(et) ||
        et.startsWith(pt) ||
        (et.length >= 4 && pt.length >= 4 && et.slice(0, 4) === pt.slice(0, 4))
      ) {
        return true;
      }
    }
    // fused page name with no spaces (e.g. "Way2VAT") — substring check
    if (et.length >= 4 && pageJoined.includes(et)) return true;
  }
  return false;
}
